/**
 * useReaderFontSize Hook - MA-ARCH-161
 * 
 * Extracted from ScriptPlay.jsx to isolate reader font size controls.
 * Persists font delta to localStorage.
 * 
 * Usage:
 * const { fontSize, fontDelta, increase, decrease, reset } = useReaderFontSize(baseFontSize);
 */

import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = "missionassist_reader_font_delta";
const MIN_DELTA = -4;
const MAX_DELTA = 8;

export function useReaderFontSize(baseFontSize = 16) {
  const [fontDelta, setFontDelta] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const n = raw ? parseInt(raw, 10) : 0;
      return Number.isFinite(n) ? n : 0;
    } catch {
      return 0;
    }
  });

  // Persist to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(fontDelta));
    } catch {
      // ignore
    }
  }, [fontDelta]);

  // Computed font size
  const fontSize = baseFontSize + fontDelta;

  // Increase font size
  const increase = useCallback(() => {
    setFontDelta((prev) => Math.min(prev + 2, MAX_DELTA));
  }, []);

  // Decrease font size
  const decrease = useCallback(() => {
    setFontDelta((prev) => Math.max(prev - 2, MIN_DELTA));
  }, []);

  // Reset to base
  const reset = useCallback(() => {
    setFontDelta(0);
  }, []);

  return {
    fontSize,
    fontDelta,
    setFontDelta,
    increase,
    decrease,
    reset,
    canIncrease: fontDelta < MAX_DELTA,
    canDecrease: fontDelta > MIN_DELTA,
  };
}

export default useReaderFontSize;
